import { Link } from "react-router-dom";
import { Phone, Mail, Facebook, Linkedin, Instagram, Youtube, ArrowUpRight } from "lucide-react";
import { SITE } from "@/lib/site";
import Logo from "@/components/Logo";

const cols = [
  {
    title: "Services",
    links: [
      ["Credit Card Processing", "/services#credit-card-processing"],
      ["Cash Discount / Dual Pricing", "/cash-discount"],
      ["Traditional Processing", "/services#traditional-processing"],
      ["E-Commerce Payments", "/services#ecommerce"],
      ["Mobile Payments", "/services#mobile-payments"],
      ["Virtual Terminal", "/services#virtual-terminal"],
    ],
  },
  {
    title: "Products",
    links: [
      ["Terminals", "/products"],
      ["Mobile Devices", "/mobile-devices"],
      ["POS Systems", "/pos-systems"],
      ["Free POS Program", "/free-pos-program"],
    ],
  },
  {
    title: "Resources",
    links: [
      ["Blog", "/resources"],
      ["FAQ", "/resources/faq"],
    ],
  },
  {
    title: "Company",
    links: [
      ["About Us", "/about"],
      ["Partnerships", "/partnerships"],
      ["Contact", "/contact"],
      ["Syracuse, NY", "/merchant-services-syracuse-ny"],
      ["New Application", "/savings-analysis"],
    ],
  },
];

export default function Footer() {
  return (
    <footer className="bg-[var(--swipe-navy-deep)] text-white/80 relative overflow-hidden">
      <div className="container-swipe relative">
        {/* CTA strip */}
        <div className="py-10 border-b border-white/10 grid lg:grid-cols-[1.5fr,1fr] gap-8 items-center">
          <div>
            <h3 className="font-display-tight text-2xl md:text-3xl font-bold text-white tracking-tight">
              Let&apos;s see what your business could be saving.
            </h3>
            <p className="mt-2 text-white/60 text-[15px] max-w-xl">
              Free, no-pressure savings analysis. Most reviews take under 10 minutes.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row lg:justify-end gap-3">
            <Link to="/savings-analysis" className="btn-primary-swipe" data-testid="footer-cta-savings">
              Get Free Savings Analysis
            </Link>
            <Link to="/contact" className="btn-outline-light" data-testid="footer-cta-contact">
              Contact Us
            </Link>
          </div>
        </div>

        {/* Main grid */}
        <div className="py-14 grid gap-10 md:grid-cols-2 lg:grid-cols-[1.5fr,1fr,1fr,1fr,1.1fr]">
          <div>
            <Logo variant="light" className="w-[310px] h-auto -ml-1" linkClassName="" testId="footer-logo" />
            <p className="mt-5 text-sm text-white/65 leading-relaxed max-w-xs">
              Merchant processing and POS solutions for businesses. Lower fees, faster
              checkout, and dependable support — every step of the way.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <a href={SITE.socials.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center hover:bg-white hover:text-[var(--swipe-navy)] transition-colors"><Facebook className="w-4 h-4" /></a>
              <a href={SITE.socials.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center hover:bg-white hover:text-[var(--swipe-navy)] transition-colors"><Linkedin className="w-4 h-4" /></a>
              <a href={SITE.socials.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center hover:bg-white hover:text-[var(--swipe-navy)] transition-colors"><Instagram className="w-4 h-4" /></a>
              <a href={SITE.socials.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center hover:bg-white hover:text-[var(--swipe-navy)] transition-colors"><Youtube className="w-4 h-4" /></a>
            </div>
          </div>

          {cols.map((col) => (
            <div key={col.title}>
              <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[var(--swipe-accent)]">{col.title}</div>
              <ul className="mt-5 space-y-3">
                {col.links.map(([label, to]) => (
                  <li key={label}>
                    <Link to={to} className="text-sm text-white/70 hover:text-white inline-flex items-center gap-1 group">
                      {label}
                      <ArrowUpRight className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[var(--swipe-accent)]">Contact</div>
            <ul className="mt-5 space-y-3 text-sm">
              <li className="flex items-start gap-2">
                <Phone className="w-4 h-4 text-white/60 mt-0.5" />
                <a href={`tel:${SITE.phoneRaw}`} className="hover:text-white">{SITE.phone}</a>
              </li>
              <li className="flex items-start gap-2">
                <Mail className="w-4 h-4 text-white/60 mt-0.5" />
                <a href={`mailto:${SITE.email}`} className="hover:text-white">{SITE.email}</a>
              </li>
            </ul>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="py-7 border-t border-white/10 grid gap-4 md:flex md:items-center md:justify-between text-[12px] text-white/45">
          <div className="max-w-3xl leading-relaxed">
            Swipetronics provides merchant processing and POS solutions. Program availability,
            pricing, equipment offers, and savings depend on business type, approval, processing
            volume, applicable rules, and underwriting.
          </div>
          <div className="flex items-center gap-5 shrink-0">
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
            <span>© 2026 Swipetronics. All rights reserved.</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
